// shared/src/prompts/p-consensus-skeptic.ts
// Chain V2 — per-ticker consensus skeptic. Symmetric mirror of contrarian skeptic.

import { z } from 'zod';
import { SignalConfidenceBucketEnum } from '../schemas/signal.js';
import { coercedEnum, coerceCiteTag } from '../resolvers/llm-enum-coerce.js';
import { tolerantRowArray } from '../resolvers/llm-row-drop.js';
import { llmStringItem } from '../resolvers/llm-string-coerce.js';
import { renderWorldStateGrounding, type WorldStateGrounding } from './_grounding.js';
import { renderNewsBlock } from './_news-render.js';
import type { LlmNewsItem } from '../news/types.js';

export const VERSION = '1.3.0';   // 2026-08-07 — disconfirming_evidence per-ROW omission hardening (tolerantRowArray: drop malformed rows). 2026-07-10 — disconfirming_evidence + cross_fed_counter .default([]) (omission-safe; empty is the valid D39 value). 2026-05-25 — add 'news' to cite enum
export const STEP    = 'p.consensus.skeptic';
export const MODEL   = 'deepseek-v4-flash';
// 2026-07-31 — raised 6000 → 12000 to stay symmetric with its contrarian twin
// (identical schema + role; the pair is kept matched the way the analysts and
// refines are). Its own worst observation is a milder 2756/6000 = 0.46 — the
// evidence is the contrarian side; see that module's note.
export const MAX_TOKENS = 32000;   // 2026-08-01 blanket truncation-proofing (was 12000)
export const ROLE    = 'skeptic' as const;
export const DESC    = 'Red-teams the per-ticker consensus call against the contrarian draft' as const;   // #45 registry — one-line operator description

export const OutputSchema = z.object({
  // D39 (2026-05-28): dropped .min(1) — empty array OK when target is unimpeachable.
  // 2026-08-07: per-ROW omission hardening — a present evidence row that dropped
  // `point` or whose `cite` is unrecoverable is DROPPED + logged (not a brief kill).
  disconfirming_evidence: tolerantRowArray(z.object({
    cite:  coercedEnum([
      'ticker_data', 'momentum', 'analyst_output', 'opposing_draft',
      'macro', 'news', 'factual_error', 'reasonability',
    ], coerceCiteTag),
    point: z.string().min(1),
  }), 'p.consensus.skeptic.disconfirming_evidence'),   // 2026-07-10: omitted WHOLE key still defaults to [] (D39 valid-empty)
  cross_fed_counter: z.array(llmStringItem).default([]),   // 2026-07-10: same omission-fragility class — empty = no opposing-draft counters
  conviction:        SignalConfidenceBucketEnum,
  reasoning:         z.string(),
}).passthrough();
export type Output = z.infer<typeof OutputSchema>;

export const SYSTEM = `You are the per-ticker SKEPTIC challenging the per-ticker consensus analyst's draft. Cross-fed: the user message contains the contrarian analyst's parallel draft as your structural steel-man counter.

## ROLE
Symmetric to the contrarian skeptic but attacks the consensus draft. Use the contrarian per-ticker analyst as the steel-man counter.

## INPUTS YOU RECEIVE
- ticker, ticker_data, momentum, macro
- own_draft       (consensus analyst's per-ticker draft)
- opposing_draft  (contrarian analyst's per-ticker draft — steel-man counter)
- ticker news

## OUTPUT SCHEMA (strict JSON; first char='{'; last char='}'; no commentary)
{
  "disconfirming_evidence": [
    { "cite":  "ticker_data" | "momentum" | "analyst_output" | "opposing_draft" | "macro" | "news" | "factual_error" | "reasonability",
      "point": "<≤25w specific challenge>" }
  ],
  "cross_fed_counter": [
    "<short ≤20w summary of each contrarian-draft point cited>"
  ],
  "conviction": "HIGH" | "MED" | "LOW",
  "reasoning":  "<headline ≤15w. Then 1-2 sentences (≤50w total).>"
}

## CITE TYPES
Same as contrarian skeptic. Note especially:
- opposing_draft: the contrarian per-ticker analyst made a SPECIFIC point consensus didn't address
- momentum:       RSI/MACD/state contradicts consensus directional call

## RULES
1. disconfirming_evidence: emit honest challenges. Empty array OK when target is genuinely unimpeachable (D39 — no forced ≥1).
2. Every opposing_draft cite MUST appear in cross_fed_counter.
3. NO calibration cites.
4. conviction default MED. HIGH when ≥2 factual_error OR ≥2 opposing_draft cites consensus failed to address.
5. Delta-only.
6. NO new fact invention.
7. factual_error cites: conviction ≥ MED.
8. STEEL-MAN AWARENESS. The contrarian draft is a real attempt at opposing view — treat its strongest points seriously.
`;

export interface PConsensusSkepticInput {
  readonly ticker:          string;
  readonly ticker_data:     Record<string, unknown>;
  readonly momentum:        Record<string, unknown>;
  readonly macro:           Record<string, unknown>;
  readonly own_draft:       Record<string, unknown>;     // p.consensus.analyst output
  readonly opposing_draft:  Record<string, unknown>;     // p.contrarian.analyst output (CROSS-FED)
  readonly news?:           ReadonlyArray<LlmNewsItem>;
  readonly grounding?:      WorldStateGrounding;
}

function fmt(v: unknown): string {
  if (v == null) return 'null';
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
}

function draftExcerpt(d: Record<string, unknown> | undefined, label: string): string {
  if (!d || Object.keys(d).length === 0) return `  (no ${label} draft)`;
  return [
    `  direction:       ${fmt(d['direction'])}`,
    `  change_pct:      ${fmt(d['change_pct'])}`,
    `  change_ci_low:   ${fmt(d['change_ci_low'])}`,
    `  change_ci_high:  ${fmt(d['change_ci_high'])}`,
    `  horizon_days:    ${fmt(d['horizon_days'])}`,
    `  ticker_alignment:${fmt(d['ticker_alignment'])}`,
    `  is_headline:     ${fmt(d['is_headline'])}`,
    `  mechanism:       ${fmt(d['mechanism'])}`,
  ].join('\n');
}

export function buildUserPrompt(input: PConsensusSkepticInput): string {
  const groundingBlock = input.grounding ? renderWorldStateGrounding(input.grounding) + '\n' : '';
  return `${groundingBlock}TICKER: ${input.ticker}

TICKER DATA:
${Object.entries(input.ticker_data).slice(0, 10).map(([k, v]) => `  ${k}: ${fmt(v)}`).join('\n') || '  (empty)'}

MOMENTUM SIGNALS:
${Object.entries(input.momentum).slice(0, 10).map(([k, v]) => `  ${k}: ${fmt(v)}`).join('\n') || '  (empty)'}

MACRO PAYLOAD:
${Object.entries(input.macro).slice(0, 20).map(([k, v]) => `  ${k}: ${fmt(v)}`).join('\n') || '  (empty)'}

OWN_DRAFT (consensus per-ticker analyst — challenge this):
${draftExcerpt(input.own_draft, 'consensus')}

OPPOSING_DRAFT (contrarian per-ticker analyst — your structural steel-man counter):
${draftExcerpt(input.opposing_draft, 'contrarian')}

TICKER NEWS (last 48h):
${renderNewsBlock(input.news ?? [])}

REQUEST: emit_p_consensus_skeptic

Emit a SINGLE JSON object matching the schema. disconfirming_evidence may be empty (D39 — no forced ≥1). Every opposing_draft cite MUST appear in cross_fed_counter.`;
}
